import React, { useState } from 'react';
import { Chip } from '../../lib/types';
import { formatDate, getAdopterDisplayName } from '../../lib/utils';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import DetailSection from '../../components/DetailSection/DetailSection';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import Button from '../../components/Button/Button';
import ChipHistoryTimeline from './ChipHistoryTimeline';

interface ChipDetailModalProps {
  open: boolean;
  onClose: () => void;
  chip: Chip | null;
  loading?: boolean;
  onTransfer: (chip: Chip) => void;
  onRecall: (chip: Chip) => void;
}

export default function ChipDetailModal({ open, onClose, chip, loading, onTransfer, onRecall }: ChipDetailModalProps) {
  const [showHistory, setShowHistory] = useState(false);

  if (!chip) {
    return null;
  }

  function handleClose() {
    setShowHistory(false);
    onClose();
  }

  function handleTransfer() {
    handleClose();
    setTimeout(() => onTransfer(chip!), 200);
  }

  function handleRecall() {
    handleClose();
    setTimeout(() => onRecall(chip!), 200);
  }

  const hasOwner = !!chip.adopterFirstName;

  const fullAddress = chip.adopterStreet
    ? [
        chip.adopterStreet,
        chip.adopterApt ? `Apt ${chip.adopterApt}` : '',
        `${chip.adopterCity}, ${chip.adopterState} ${chip.adopterZip}`,
      ]
        .filter(Boolean)
        .join(', ')
    : null;

  return (
    <Modal open={open} onClose={handleClose} size="wide">
      {showHistory ? (
        <ChipHistoryTimeline
          history={chip.history}
          chipLabel={chip.petName || chip.id}
          chipId={chip.id}
          onBack={() => setShowHistory(false)}
        />
      ) : (
        <>
          <ModalTitle>Chip details</ModalTitle>

          <DetailSection
            title="Pet Information"
            fields={[
              { label: 'Pet Name', value: chip.petName },
              { label: 'Species', value: chip.species },
              { label: 'Breed', value: chip.breed ? `${chip.breed}${chip.mixedBreed ? ' (mixed)' : ''}` : null },
              { label: 'Gender', value: chip.gender },
              { label: 'Date of Birth', value: formatDate(chip.dob) },
              { label: 'Spayed/Neutered', value: chip.spayedNeutered },
              { label: 'Color/Markings', value: chip.color },
              { label: 'Weight', value: chip.weight ? `${chip.weight} lbs` : null },
              ...(chip.additionalInfo ? [{ label: 'Additional Info', value: chip.additionalInfo }] : []),
            ]}
          />

          {hasOwner ? (
            <DetailSection
              title="Owner"
              fields={[
                {
                  label: 'Name',
                  value: [chip.adopterFirstName, chip.adopterLastName].filter(Boolean).join(' ') || null,
                },
                { label: 'Email', value: chip.adopterEmail },
                { label: 'Phone', value: chip.adopterPhone },
                { label: 'Address', value: fullAddress },
                ...(chip.assignedTo === 'rescue' ? [{ label: 'Status', value: 'Rescue (in care)' }] : []),
              ]}
            />
          ) : (
            <DetailSection title="Owner" fields={[{ label: 'Status', value: 'No owner assigned' }]} />
          )}

          <DetailSection
            title="Chip"
            fields={[
              { label: 'Chip ID', value: chip.id },
              { label: 'Full ID', value: chip.fullChipId },
              {
                label: 'Status',
                value: (
                  <StatusBadge variant={chip.status === 'active' ? 'success' : 'neutral'}>
                    {chip.status === 'active' ? 'Active' : 'Stock'}
                  </StatusBadge>
                ),
              },
              { label: 'Registered', value: formatDate(chip.registeredDate) },
              { label: 'Last Updated', value: formatDate(chip.lastUpdated) },
            ]}
          />

          <ModalActions>
            {chip.history.length > 0 && (
              <Button variant="ghost" onClick={() => setShowHistory(true)}>
                History
              </Button>
            )}
            {!hasOwner && (
              <Button variant="green" onClick={handleTransfer}>
                Add Owner
              </Button>
            )}
            {hasOwner && (
              <Button variant="danger" onClick={handleRecall}>
                Clear Owner
              </Button>
            )}
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
          </ModalActions>
        </>
      )}
    </Modal>
  );
}
